import React, { useState, useEffect } from "react";
import { useApolloClient } from "@apollo/react-hooks";
import { FINICITY_CONNECT_LINK_QUERY } from "utils/mutations";

const FinicityConnect = ({
  linkURL,
  buttonClassName = `rounded px-4 py-2 ml-4 bg-transparent text-black`,
  buttonText = `+ add new account`,
  onSuccess = () => {},
  onCancel = () => {},
  onError = () => {},
  onLoaded = () => {},
  onRouteEvent = () => {},
  onUserEvent = () => {},
  allowShowMore = false,
  showMoreAccounts = () => {},
  show: initialShow = false,
  hidden,
  disabled,
  aggregation,
  openFinicity,
  setOpenFinicity,
}) => {
  const [link, setLink] = useState(linkURL);
  const [fetching, setFetching] = useState(false);

  const [show, setShow] = useState(initialShow);
  const [showing, setShowing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showMoreAccountsPrompt, setShowMoreAccountsPrompt] = useState(false);

  useEffect(() => {
    if (!link && !fetching) { fetchLink(); }
  }, [link, fetching]);

  useEffect(() => {
    if (show === false && window.finicityConnect) { destroy(); }
  }, [show]);

  useEffect(() => {
    if (show && !showing && !!link) { 
      launch();
    }
  }, [show, showing, link]);

  useEffect(() => {
    if (openFinicity) {
      setShow(true);
      setOpenFinicity(false);
    }
  }, [openFinicity]);

  const client = useApolloClient();

  const fetchLink = async () => {
    setFetching(true);
    const queryPayload = {
      query: FINICITY_CONNECT_LINK_QUERY,
    };
    if (aggregation) {
      queryPayload.variables = { aggregation };
    }
    const results = await client.query(queryPayload);
    const { data: { finicityConnect } = {} } = results || {};
    const { link: fetchedLink } = finicityConnect || {};
    if (fetchedLink) {
      setLink(fetchedLink);
      setFetching(false);
    }
  };

  const destroy = () => {
    if (window.finicityConnect) {
      window.finicityConnect.destroy();
      setShowing(false);
      setShowMoreAccountsPrompt(false);
    }
  };

  const launch = async () => {
    destroy();
    setLoading(true);
    window.finicityConnect.launch(link, {
      selector: "#connect-container",
      success: (event) => {
        setShow(false);
        console.log("FinicityConnect - success");
        onSuccess(event);
      },
      cancel: (event) => {
        setShow(false);
        console.log("FinicityConnect - cancel");
        onCancel(event);
      },
      error: (error) => {
        setShow(false);
        console.error("FinicityConnect - error");
        onError(error);
      },
      loaded: () => {
        console.log("FinicityConnect - loaded");
        setLoading(false);
        setShowing(true);
        onLoaded();
      },
      route: (event) => {
        console.log("FinicityConnect - route event");
        const { data: { screen, params = {} } = {} } = event;
        if (screen === "sign-in") { setShowMoreAccountsPrompt(false); } else if (screen === "search" && params.query) { setShowMoreAccountsPrompt(true); }
        onRouteEvent(event);
      },
      user: (event) => {
        console.log("FinicityConnect - user event");
        const { data: { action } = {} } = event;
        if (action === "GetInstitutionsSuccess") { setShowMoreAccountsPrompt(true); }
        onUserEvent(event);
      },
    });
  };

  const renderShow = () => (
    <div>
      <div id="connect-container">
        {loading ? (
          <i
            className="fas fa-circle-notch fa-spin absolute left-0 right-0 top-0 bottom-0 m-auto text-4xl"
            style={{ height: 35, width: 40 }}
          />
        ) : (
          <></>
        )}
        {allowShowMore && showMoreAccountsPrompt && (
          <div
            id="moreAccountsPrompt"
            className="bg-red-500 hover:bg-red-600 text-white cursor-pointer"
            onClick={() => {
              setShow(false);
              showMoreAccounts();
            }}
          >
            Don't see your bank?
          </div>
        )}
      </div>
    </div>
  );

  const renderDefault = () =>
    (hidden ? (
      <></>
    ) : (
      <div
        className={`${buttonClassName} ${
          link && !disabled
            ? "text-black cursor-pointer"
            : "text-gray-secondary cursor-not-allowed"
        }`}
        onClick={() => {
          if (!link || disabled) { return; }
          if (link) { setShow(true); } else {
            console.log(
              "Unable to show FinicityConnect because no linkURL was provided",
            );
          }
        }}
      >
        {buttonText}
        {!link && (
          <i className="ml-2 fas fa-circle-notch fa-spin" />
        )}
      </div>
    ));

  return showing ? renderShow() : renderDefault();
};

export default FinicityConnect;
